/**
 * 문서관리 — 영업건 첨부 문서 모아보기 탭 (고객 발주서 / 분할납품요구서 공용).
 *
 * 영업건(parent_kind='sales')에 첨부된 특정 kind 문서 전체를 영업건 정보와 함께 목록으로.
 *   - [등록] → 영업건을 검색·선택하고 PDF 첨부 → 그 영업건의 첨부로 저장.
 *     단일 원본이라 영업건 상세에서도 동일하게 보이고 다운로드됨 (중복 없음).
 *   - 행 클릭 = 미리보기(팝업) · [⬇ 저장] · [🗑 삭제]  (v2.112.0: 중복이던 [👁 미리보기] 버튼 제거)
 *
 * 고객 발주서(kind='발주서')는 영업건당 1개 정책 → 등록 시 replace.
 * 분할납품요구서(kind='분할납품요구서')는 여러 개 허용.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  listAttachmentsByKind,
  uploadAttachment,
  deleteAttachment,
  previewAttachment,
  downloadAttachment,
  type SalesDocRow,
} from '../../lib/attachment-api';
import { SalesSearchSelect } from '../../components/SalesSearchSelect';
import type { SalesListRow } from '../../lib/types';
import { useAuth } from '../../lib/auth-context';
import { statusTagClass } from '../../lib/sales-stage';
import { LoadingLines } from '../../components/ui/Skeleton';

function fmtSize(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
  if (n >= 1024) return `${Math.round(n / 1024)}KB`;
  return `${n}B`;
}
function fmtDate(s: string | null): string {
  if (!s) return '·';
  return s.slice(0, 10);
}


export function SalesDocsTab({
  kind,
  singlePerSales,
  noun,
  accept = '.pdf,application/pdf',
  fileLabel = 'PDF',
}: {
  kind: string;          // '발주서' | '분할납품요구서' | '기타 관련서류'
  singlePerSales: boolean; // true 면 등록 시 영업건당 1개로 교체
  noun: string;          // 안내 문구용 (예: '고객 발주서')
  accept?: string;       // 등록 모달의 파일 형식 (기타 관련서류는 PDF+엑셀+이미지)
  fileLabel?: string;    // 파일 입력 라벨 (예: 'PDF' / 'PDF·엑셀·이미지')
}) {
  const { user } = useAuth();
  const canEdit = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';

  const q = useQuery({
    queryKey: ['salesDocs', kind],
    queryFn: () => listAttachmentsByKind(kind),
    staleTime: 30_000,
  });
  const docs = q.data ?? [];

  const [showReg, setShowReg] = useState(false);

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="ui-section-title">{noun} 목록</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            영업진행 건에 첨부된 {noun} 전체 — 행 클릭으로 미리보기 후 다운로드. 등록 시 영업건에 연동돼 영업건 상세에서도 보입니다.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">{docs.length}건</span>
          {canEdit && (
            <button
              type="button"
              onClick={() => setShowReg(true)}
              className="btn btn-primary"
            >
              + 등록 (영업건 연동)
            </button>
          )}
        </div>
      </div>

      {q.isLoading ? (
        <div className="p-10 text-center text-slate-400 text-sm"><LoadingLines /></div>
      ) : q.isError ? (
        <div className="p-10 text-center text-red-500 text-sm">오류: {(q.error as Error).message}</div>
      ) : docs.length === 0 ? (
        <div className="p-10 text-center text-slate-400 text-sm">등록된 {noun}가 없습니다.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table w-full text-[13px] min-w-[920px]">
            <thead>
              <tr className="bg-slate-50 text-slate-600 border-y border-slate-200">
                <th className="px-3 py-2 text-left font-semibold">영업건</th>
                <th className="px-3 py-2 text-center font-semibold">상태</th>
                <th className="px-3 py-2 text-left font-semibold">엔드유저</th>
                <th className="px-3 py-2 text-left font-semibold">건명</th>
                <th className="px-3 py-2 text-left font-semibold">담당자</th>
                <th className="px-3 py-2 text-left font-semibold">파일명</th>
                <th className="px-3 py-2 text-right font-semibold">크기</th>
                <th className="px-3 py-2 text-center font-semibold">업로드일</th>
                <th className="px-3 py-2 text-center font-semibold">저장</th>
                {canEdit && <th className="px-3 py-2 text-center font-semibold">삭제</th>}
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <DocRow key={d.id} d={d} canEdit={canEdit} onChanged={() => q.refetch()} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showReg && (
        <RegisterModal
          kind={kind}
          singlePerSales={singlePerSales}
          noun={noun}
          accept={accept}
          fileLabel={fileLabel}
          onClose={() => setShowReg(false)}
          onDone={() => {
            setShowReg(false);
            q.refetch();
          }}
        />
      )}
    </div>
  );
}

function DocRow({ d, canEdit, onChanged }: { d: SalesDocRow; canEdit: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  async function onDelete() {
    if (!window.confirm(`${d.filename}\n\n이 문서를 삭제할까요? (영업건 ${d.sales_id} 에서도 사라집니다)`)) return;
    setBusy(true);
    try {
      await deleteAttachment(d.id);
      onChanged();
    } catch (e) {
      alert(`삭제 실패: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }
  return (
    // v2.112.0 — 행 아무 데나 눌러도 미리보기 (견적서·발주서 탭과 같은 손놀림).
    //   버튼이 든 칸은 stopPropagation 으로 막아 「미리보기」가 두 번 뜨지 않게 한다.
    <tr
      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
      onClick={() => previewAttachment(d)}
      title="클릭하면 미리보기"
    >
      <td className="px-3 py-1.5 font-mono font-semibold text-blue-700 whitespace-nowrap">{d.sales_id}</td>
      <td className="px-3 py-1.5 text-center">
        {/* 영업진행·영업건 검색과 같은 단계 색 (v2.309.0 · #22 — 전엔 이 탭만 납품=남색·발행=초록이라 검색 칸과 뜻이 엇갈렸다) */}
        <span className={statusTagClass(d.status) ?? ''}>{d.status}</span>
      </td>
      <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{d.end_user ?? '·'}</td>
      <td className="px-3 py-1.5 text-slate-600 max-w-[260px] truncate" title={d.project_name ?? ''}>
        {d.project_name ?? '·'}
      </td>
      <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{d.emp_name ?? '·'}</td>
      <td className="px-3 py-1.5 text-slate-600 max-w-[220px] truncate" title={d.filename}>{d.filename}</td>
      <td className="px-3 py-1.5 text-right text-slate-400 whitespace-nowrap">{fmtSize(d.file_size)}</td>
      <td className="px-3 py-1.5 text-center text-slate-400 whitespace-nowrap">{fmtDate(d.uploaded_at)}</td>
      {/* v2.112.0 — [👁 미리보기] 버튼은 뺐다. 행 아무 데나 누르면 같은 창이 뜨므로 중복이었다
          (2026-09-01 사용자 지시). 다운로드는 다른 일이라 남긴다. */}
      <td className="px-3 py-1.5 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => downloadAttachment(d.id, d.filename)}
          className="btn btn-secondary btn-sm"
          title="다운로드"
        >
          ⬇ 저장
        </button>
      </td>
      {canEdit && (
        <td className="px-3 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            disabled={busy}
            onClick={onDelete}
            className="btn btn-danger btn-sm"
          >
            🗑
          </button>
        </td>
      )}
    </tr>
  );
}

function RegisterModal({
  kind,
  singlePerSales,
  noun,
  accept,
  fileLabel,
  onClose,
  onDone,
}: {
  kind: string;
  singlePerSales: boolean;
  noun: string;
  accept: string;
  fileLabel: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [sel, setSel] = useState<{
    salesId: string; endUser: string | null; proposalCompany: string | null; projectName: string | null; status: string;
  } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit() {
    if (!sel) { setErr('영업건을 선택하세요.'); return; }
    if (!file) { setErr(`첨부할 ${fileLabel} 파일을 선택하세요.`); return; }
    setBusy(true);
    setErr(null);
    try {
      await uploadAttachment('sales', sel.salesId, kind, file, { replace: singlePerSales });
      onDone();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="ui-subsection-title mb-1">{noun} 등록 — 영업건 연동</h3>
        <p className="text-xs text-slate-500 mb-4">
          연동할 영업건을 선택하고 {fileLabel} 파일을 첨부하세요. 저장하면 그 영업건의 첨부로 등록돼 영업건 상세에서도 다운로드됩니다.
          {singlePerSales && ' (고객 발주서는 영업건당 1개 — 기존 파일이 있으면 교체됩니다.)'}
        </p>

        <label className="ui-label">영업건</label>
        <div className="mt-1 mb-4">
          <SalesSearchSelect
            selected={sel}
            onSelect={(row: SalesListRow) =>
              setSel({
                salesId: row.salesId,
                endUser: row.endUser,
                proposalCompany: row.proposalCompany,
                projectName: row.projectName,
                status: row.status,
              })
            }
            onClear={() => setSel(null)}
            placeholder="매출일련번호 / 엔드유저 / 제안사 / 건명 검색"
          />
        </div>

        <label className="ui-label">{noun} 파일 ({fileLabel})</label>
        <input
          type="file"
          accept={accept}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="mt-1 mb-4 block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
        />

        {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            취소
          </button>
          <button
            type="button"
            disabled={busy || !sel || !file}
            onClick={onSubmit}
            className="btn btn-primary"
          >
            {busy ? '업로드 중…' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
}
